import { ExternalLink, FileText, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import type { CheckInEvidence } from '../../../../../packages/contracts/src/index.js';
import { api } from '../../shared/api';
import { formatDateTime } from '../../shared/format';
import { Button } from '../ui/button';
import { ConfirmDialog, type ConfirmState } from '../ui/confirm';

export function LegalDocumentsDialog({
  documents,
  title,
  onClose,
  onClear,
}: {
  readonly documents: readonly CheckInEvidence[];
  readonly title: string;
  readonly onClose: () => void;
  readonly onClear: () => Promise<void> | void;
}) {
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  const openDocument = async (document: CheckInEvidence) => {
    setError('');
    try {
      const download = await api.files.createDownload(document.objectKey);
      window.open(download.downloadUrl, '_blank', 'noreferrer');
    } catch (openError) {
      setError(openError instanceof Error ? openError.message : 'No se pudo abrir el documento.');
    }
  };

  const clearDocuments = async () => {
    setIsClearing(true);
    try {
      await onClear();
      onClose();
    } catch (clearError) {
      setError(clearError instanceof Error ? clearError.message : 'No se pudieron limpiar los documentos.');
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/35 p-4">
      <div className="w-full max-w-lg rounded-md border border-[#d6ded0] bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#eef3ea] p-4">
          <div>
            <h2 className="text-base font-semibold text-slate-950">Documentos legales</h2>
            <p className="mt-1 text-sm text-[#687461]">{title}</p>
          </div>
          <button type="button" className="rounded-md p-1 text-[#687461] hover:bg-[#eef3ea]" aria-label="Cerrar" onClick={onClose}>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="grid gap-3 p-4">
          {documents.length === 0 ? <p className="text-sm text-[#687461]">No hay documentos de entrada registrados.</p> : null}
          {documents.map((document) => (
            <div key={`${document.fileId}-${document.objectKey}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-semibold text-[#334033]">
                  <FileText className="h-4 w-4" aria-hidden="true" />
                  {document.kind === 'signature' ? 'Firma digital' : 'Documento'}
                </p>
                <p className="mt-1 truncate text-xs text-[#687461]">{document.fileName} · {formatDateTime(document.capturedAt)}</p>
              </div>
              <Button type="button" variant="secondary" onClick={() => openDocument(document)}>
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                Ver
              </Button>
            </div>
          ))}
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cerrar</Button>
            <Button
              type="button"
              variant="danger"
              disabled={documents.length === 0 || isClearing}
              onClick={() => setConfirm({
                title: 'Limpiar documentos legales',
                body: 'Se quitaran las firmas y documentos asociados a esta entrada. Los archivos existentes dejaran de mostrarse en el registro.',
                confirmLabel: 'Limpiar documentos',
                onConfirm: clearDocuments,
              })}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Limpiar documentos
            </Button>
          </div>
        </div>
      </div>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
